import React, { useEffect, useState } from 'react';
import GrowthDashboard from './GrowthDashboard';
import LearningEngine from './LearningEngine';
import CorpusHealth from './CorpusHealth';
import AdminRoles from './AdminRoles';
import FeedbackCenter from './FeedbackCenter';
import { getParam, setParams } from '../urlState';

const API = 'https://tm-audit-platform-production.up.railway.app';
const PLANS = ['free', 'tm', 'xactimate', 'all', 'beta'];

// The two surfaces the founder actually works from stay top-level: USERS (who's signed
// in, how active) and INVITES (bring a tester on). Everything else is real but
// rarely touched, so it lives behind a collapsed Advanced disclosure — hidden,
// never removed (Learning + Corpus back the morning Learning-Pipeline KPIs).
const PRIMARY_TABS = ['users', 'invites'];
const ADVANCED_TABS = ['growth', 'feedback', 'learning', 'corpus-health', 'stats', 'admins', 'audit-log', 'consent', 'corpus'];
const TABS = [...PRIMARY_TABS, ...ADVANCED_TABS];
const TAB_LABELS = {
    users: 'Users', invites: 'Invites', growth: 'Growth dashboard', feedback: 'Feedback',
    learning: 'Learning engine', 'corpus-health': 'Audee intelligence', stats: 'Overview',
    admins: 'Admins', 'audit-log': 'Audit log', consent: 'Consent', corpus: 'Corpus',
};

function AdminDashboard({ onBack, session }) {
    // ?tab=<tab> — the param urlState.js has documented since it was written, now
    // actually implemented, so a refresh inside the admin area returns you to the
    // tab you were on instead of dropping you on the default. Validated against
    // TABS: the URL is user input, and an unknown value must fall back rather than
    // render a blank dashboard.
    const [activeTab, setActiveTab] = useState(() => {
        const t = getParam('tab');
        return TABS.includes(t) ? t : 'users';
    });
    // Advanced starts collapsed, but opens itself when a refresh lands directly on
    // an advanced tab so the active view is never hidden behind a closed toggle.
    const [advancedOpen, setAdvancedOpen] = useState(() => ADVANCED_TABS.includes(getParam('tab')));

    const selectTab = (tab) => {
        setActiveTab(tab);
        if (ADVANCED_TABS.includes(tab)) setAdvancedOpen(true);
        // Leaving a tab drops the sub-view param with it — ?sub belongs to whichever
        // tab owns it, and carrying it across would apply it to a tab that has none.
        setParams({ tab: tab === 'users' ? null : tab, sub: null });
    };
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [consentStats, setConsentStats] = useState(null);
    const [corpus, setCorpus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planUpdating, setPlanUpdating] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteResult, setInviteResult] = useState(null);
    const [inviteRevoked, setInviteRevoked] = useState(false);
    const [inviteCopied, setInviteCopied] = useState(false);

    const getHeaders = () => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
    });

    useEffect(() => {
        fetchAll(); // eslint-disable-line react-hooks/exhaustive-deps
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Lazy-load the heavy corpus analytics only when the Corpus tab is opened,
    // and only once. Keeps it off the initial dashboard/Users load path.
    useEffect(() => {
        if (activeTab !== 'corpus' || corpus !== null) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(API + '/api/admin/corpus-analytics', { headers: getHeaders() });
                const data = await res.json();
                if (!cancelled) setCorpus(data);
            } catch (e) { console.error(e); }
        })();
        return () => { cancelled = true; };
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = async () => {
        setLoading(true);
        try {
            // Corpus analytics is intentionally NOT loaded here — it scans every
            // audit's parsed_data_json (seconds-slow) and used to block the whole
            // dashboard, including the Users tab. It now loads lazily when the
            // Corpus tab is opened (see effect below).
            const [statsRes, usersRes, logRes, consentRes] = await Promise.all([
                fetch(API + '/api/admin/stats', { headers: getHeaders() }),
                fetch(API + '/api/admin/users', { headers: getHeaders() }),
                fetch(API + '/api/admin/audit-log', { headers: getHeaders() }),
                fetch(API + '/api/admin/consent-stats', { headers: getHeaders() }),
            ]);
            setStats(await statsRes.json());
            setUsers(await usersRes.json());
            setAuditLog(await logRes.json());
            setConsentStats(await consentRes.json());
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleSetPlan = async (userId, plan) => {
        setPlanUpdating(userId);
        try {
            await fetch(API + '/api/admin/set-plan', {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ user_id: userId, plan })
            });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, plan } : u));
        } catch (e) {}
        setPlanUpdating(null);
    };

    const handleInviteBeta = async () => {
        setInviteLoading(true);
        setInviteResult(null);
        setInviteRevoked(false);
        setInviteCopied(false);
        try {
            const res = await fetch(API + '/api/admin/invite-beta', {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ email: inviteEmail })
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                // Preflight-guarded: the server only reports success for a link it
                // re-validated, so an error here means NO link is shown.
                setInviteResult({ error: (data.error || 'Could not create the invitation.') + (data.reference ? ` (ref ${data.reference})` : '') });
            } else {
                setInviteResult(data);
            }
        } catch (e) {
            setInviteResult({ error: e.message || 'Request failed' });
        }
        setInviteLoading(false);
    };

    // Explicit replacement (State K): revoke the shown invite by its public code.
    // The old link then fails as revoked; the admin clicks Send Beta Invite again
    // to mint a fresh one.
    const handleRevokeInvite = async () => {
        const code = inviteResult?.invite_code;
        if (!code) return;
        try {
            const res = await fetch(API + '/api/admin/invite-beta/revoke', {
                method: 'POST', headers: getHeaders(), body: JSON.stringify({ code })
            });
            if (res.ok) setInviteRevoked(true);
        } catch (_) { /* leave UI as-is */ }
    };

    const copyInviteUrl = async () => {
        if (!inviteResult?.invite_url) return;
        try { await navigator.clipboard.writeText(inviteResult.invite_url); setInviteCopied(true); } catch (_) {}
    };

    const fmt = (n) => '$' + (n || 0).toLocaleString();
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
    // Most recently active first. Never signed in (no timestamp) sinks to the bottom.
    const usersByRecentLogin = [...users].sort((a, b) => {
        const ta = a.last_sign_in ? new Date(a.last_sign_in).getTime() : 0;
        const tb = b.last_sign_in ? new Date(b.last_sign_in).getTime() : 0;
        return tb - ta;
    });
    const planColor = (p) => p === 'all' ? 'var(--amber)' : p === 'beta' ? 'var(--purple)' : p === 'xactimate' ? 'var(--blue)' : p === 'tm' ? 'var(--green)' : 'var(--text-muted)';

    return (
        <div className="history-screen">
            <div className="history-back-row" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <button className="history-back-link" onClick={onBack}>← Back</button>
                <button className="history-back-link" onClick={fetchAll}>↻ Refresh</button>
            </div>
            <div className="history-header">
                <div className="history-title">Admin</div>
            </div>

            {/* Tabs — the two the founder uses stay top-level; the rest fold into Advanced. */}
            <div className="admin-tabs">
                {PRIMARY_TABS.map(tab => (
                    <button
                        key={tab}
                        className={"admin-tab " + (activeTab === tab ? 'active' : '')}
                        onClick={() => selectTab(tab)}
                    >
                        {TAB_LABELS[tab]}
                    </button>
                ))}
                <button
                    className={"admin-tab admin-tab-advanced " + (advancedOpen ? 'open' : '')}
                    onClick={() => setAdvancedOpen(o => !o)}
                    aria-expanded={advancedOpen}
                >
                    {advancedOpen ? '▾' : '▸'} Advanced
                </button>
            </div>

            {advancedOpen && (
                <div className="admin-tabs admin-tabs-advanced">
                    {ADVANCED_TABS.map(tab => (
                        <button
                            key={tab}
                            className={"admin-tab " + (activeTab === tab ? 'active' : '')}
                            onClick={() => selectTab(tab)}
                        >
                            {TAB_LABELS[tab]}
                        </button>
                    ))}
                </div>
            )}

            {/* Growth Dashboard — internal command center (does its own fetching) */}
            {activeTab === 'growth' && <GrowthDashboard session={session} />}

            {/* Internal Feedback Center — beta feedback triage (does its own fetching) */}
            {activeTab === 'feedback' && <FeedbackCenter session={session} />}

            {/* Learning Engine — internal institutional memory (does its own fetching) */}
            {activeTab === 'learning' && <LearningEngine session={session} />}

            {/* Audee Intelligence Center — internal visibility into intelligence state/growth (does its own fetching) */}
            {activeTab === 'corpus-health' && <CorpusHealth session={session} />}

            {/* Admins — in-app role & invite management (does its own fetching) */}
            {activeTab === 'admins' && <AdminRoles session={session} />}

            {loading && activeTab !== 'growth' && activeTab !== 'feedback' && activeTab !== 'learning' && activeTab !== 'corpus-health' && activeTab !== 'admins' && activeTab !== 'invites' && <div className="history-loading"><div className="spinner" /></div>}

            {/* Stats */}
            {!loading && activeTab === 'stats' && stats && (
                <div>
                    {/* Key metrics table */}
                    <div className="admin-table-wrap" style={{marginBottom:16}}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Estimated MRR</th>
                                    <th>Total Users</th>
                                    <th>Total Audits</th>
                                    <th>Audits This Month</th>
                                    <th>Total Opportunity Found</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{color:'var(--green)',fontFamily:'var(--mono)',fontWeight:700,fontSize:16}}>{fmt(stats.estimatedMRR)}</td>
                                    <td style={{fontFamily:'var(--mono)',fontWeight:700,fontSize:16}}>{stats.totalUsers}</td>
                                    <td style={{color:'var(--amber)',fontFamily:'var(--mono)',fontWeight:700,fontSize:16}}>{stats.totalAudits}</td>
                                    <td style={{fontFamily:'var(--mono)',fontWeight:700,fontSize:16}}>{stats.monthAudits}</td>
                                    <td style={{color:'var(--green)',fontFamily:'var(--mono)',fontWeight:700,fontSize:16}}>{fmt(stats.totalDollarImpact)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {/* Subscriptions table */}
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Plan</th>
                                    <th>Subscribers</th>
                                    <th>Monthly Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(stats.planCounts).filter(([p]) => p !== 'other').map(([plan, count]) => (
                                    <tr key={plan}>
                                        <td style={{color:planColor(plan),fontFamily:'var(--mono)',fontWeight:700,textTransform:'uppercase',fontSize:11}}>
                                            {plan === 'all' ? 'All Access' : plan === 'beta' ? 'Beta' : plan === 'tm' ? 'T&M' : plan === 'xactimate' ? 'Xactimate' : plan}
                                        </td>
                                        <td style={{fontFamily:'var(--mono)',fontWeight:700}}>{count}</td>
                                        <td style={{color:'var(--green)',fontFamily:'var(--mono)',fontWeight:700}}>
                                            {fmt(count * (plan === 'all' ? 499 : plan === 'xactimate' ? 399 : plan === 'tm' ? 299 : plan === 'beta' ? 0 : 0))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Invites — promoted out of Overview to its own top-level tab. This is
                the whole beta-invite flow: mint / reuse a verified link, copy it,
                revoke it. State + handlers live at the component top, so it works
                identically wherever it's rendered. */}
            {activeTab === 'invites' && (
                <div className="admin-table-wrap">
                    <div style={{padding:'16px 18px'}}>
                        <div style={{fontWeight:700,fontSize:13,marginBottom:4,letterSpacing:'.04em',textTransform:'uppercase',color:'var(--text-muted)'}}>
                            Invite Beta User
                        </div>
                        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12}}>
                            Enter a tester's email to mint (or reuse) a verified invite link.
                        </div>
                        <div style={{display:'flex',gap:8,alignItems:'center'}}>
                            <input
                                type="email"
                                className="admin-plan-select"
                                placeholder="email@example.com"
                                value={inviteEmail}
                                onChange={e => { setInviteEmail(e.target.value); setInviteResult(null); }}
                                style={{flex:1,padding:'6px 10px'}}
                                disabled={inviteLoading}
                            />
                            <button
                                className="history-back-link"
                                onClick={handleInviteBeta}
                                disabled={inviteLoading || !inviteEmail}
                            >
                                {inviteLoading ? 'Working…' : 'Send Beta Invite'}
                            </button>
                        </div>
                        {inviteResult?.error && (
                            <div style={{color:'var(--red)',fontSize:13,marginTop:8}}>{inviteResult.error}</div>
                        )}
                        {inviteResult?.invite_url && (
                            <div style={{marginTop:12}}>
                                <div style={{fontSize:12,color:'var(--text)',marginBottom:6}}>{inviteResult.message}</div>
                                {/* State chips: verified (preflight passed), reuse vs new, and
                                    whether the tester already has working access. Tinted
                                    backgrounds carry the meaning; text stays neutral ink so
                                    nothing depends on small colored type (AA on light). */}
                                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                                    <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'var(--green-bg)',border:'1px solid var(--green-border)',color:'var(--text)'}}>Active &amp; verified</span>
                                    <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'var(--neutral-bg)',border:'1px solid var(--neutral-border)',color:'var(--text-muted)'}}>No expiration</span>
                                    {inviteResult.reused && <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'var(--neutral-bg)',border:'1px solid var(--neutral-border)',color:'var(--text-muted)'}}>Existing link reused</span>}
                                    {inviteResult.tester_has_access && <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'var(--purple-bg)',border:'1px solid var(--purple-border)',color:'var(--text)'}}>Tester already has access</span>}
                                    {inviteRevoked && <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'var(--red-bg)',border:'1px solid var(--red-border)',color:'var(--text)'}}>Old link revoked — Send again for a new one</span>}
                                </div>
                                {/* The SAME canonical URL the API returned, the recipient's email
                                    button, and the copy button all reference — no divergence. */}
                                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                                    <input
                                        readOnly
                                        value={inviteResult.invite_url}
                                        className="admin-plan-select"
                                        style={{flex:1,fontFamily:'var(--mono)',fontSize:11,padding:'6px 8px'}}
                                        onFocus={e => e.target.select()}
                                    />
                                    <button className="history-back-link" onClick={copyInviteUrl}>
                                        {inviteCopied ? 'Copied' : 'Copy Link'}
                                    </button>
                                    {inviteResult.invite_code && !inviteRevoked && (
                                        <button className="history-back-link" style={{color:'var(--red)'}} onClick={handleRevokeInvite}>
                                            Revoke
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Users — the primary view. Exactly what the founder reads: who's signed in,
                how recently, how active. Sorted most-recent-login first. */}
            {!loading && activeTab === 'users' && (
                <div>
                    <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Logins</th>
                                <th>Last login</th>
                                <th>Audits</th>
                                <th>Plan</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usersByRecentLogin.map(u => (
                                <tr key={u.id}>
                                    <td>{u.email}</td>
                                    {/* Login COUNT is not tracked anywhere — the auth layer records
                                        only last_sign_in_at, and there is no login-event table. Show
                                        an honest dash rather than invent a number. See note below. */}
                                    <td style={{ color: 'var(--text-muted)' }}>—</td>
                                    <td>{fmtDateTime(u.last_sign_in)}</td>
                                    <td>{u.audit_count}</td>
                                    <td>
                                        <select
                                            className="admin-plan-select"
                                            value={u.plan}
                                            onChange={e => handleSetPlan(u.id, e.target.value)}
                                            disabled={planUpdating === u.id}
                                        >
                                            {PLANS.map(p => (
                                                <option key={p} value={p}>{p === 'all' ? 'All Access' : p === 'beta' ? 'Beta' : p === 'tm' ? 'T&M' : p}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
                        Logins: a per-user login count isn't tracked — the auth layer records only
                        the last sign-in, shown here. A real count would need login-event logging (not built).
                    </div>
                </div>
            )}

            {/* Audit Log */}
            {!loading && activeTab === 'audit-log' && (
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Job</th>
                                <th>Document Type</th>
                                <th>Findings</th>
                                <th>Opportunity</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {auditLog.map(a => (
                                <tr key={a.id}>
                                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.user_display_name || a.user_email || '—'}</td>
                                    <td>{a.job_name}</td>
                                    <td>
                                        <span className={"history-doc-type " + (a.document_type?.startsWith('Xactimate') ? 'xactimate' : 'tm')}>
                                            {a.document_type || '—'}
                                        </span>
                                    </td>
                                    <td>{a.findings_count}</td>
                                    <td style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 700 }}>
                                        {a.dollar_impact > 0 ? fmt(a.dollar_impact) : '—'}
                                    </td>
                                    <td>{fmtDate(a.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Corpus consent statistics (read-only, Phase A) */}
            {!loading && activeTab === 'consent' && (
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Total Accounts</th>
                                <th>Opted In</th>
                                <th>Opted Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.totalAccounts ?? 0}</td>
                                <td style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.optedInAccounts ?? 0}</td>
                                <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.optedOutAccounts ?? 0}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Corpus queue statistics (read-only, Phase B) */}
                    <table className="admin-table" style={{ marginTop: 24 }}>
                        <thead>
                            <tr>
                                <th>Corpus Queued</th>
                                <th>Pending</th>
                                <th>Processed</th>
                                <th>Errored</th>
                                <th>Latest Queued</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.corpusTotal ?? 0}</td>
                                <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.corpusPending ?? 0}</td>
                                <td style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.corpusProcessed ?? 0}</td>
                                <td style={{ color: 'var(--red, #c0392b)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{consentStats?.corpusErrored ?? 0}</td>
                                <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{consentStats?.corpusLatestQueuedAt ? new Date(consentStats.corpusLatestQueuedAt).toLocaleString() : '—'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* Corpus analytics — category mix + carrier counts across saved audits */}
            {!loading && activeTab === 'corpus' && (
                <div>
                    <div className="admin-table-wrap" style={{ marginBottom: 24 }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Roofing</th>
                                    <th>Water</th>
                                    <th>Fire</th>
                                    <th>Contents</th>
                                    <th>Reconstruction</th>
                                    <th>Unknown</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Roofing ?? 0}</td>
                                    <td style={{ color: 'var(--blue)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Water ?? 0}</td>
                                    <td style={{ color: 'var(--amber)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Fire ?? 0}</td>
                                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Contents ?? 0}</td>
                                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Reconstruction ?? 0}</td>
                                    <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.categories?.Unknown ?? 0}</td>
                                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>{corpus?.total ?? 0}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div style={{ fontWeight: 700, fontSize: 13, margin: '0 0 10px 2px', letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        Carriers ({corpus?.withCarrier ?? 0} detected · {corpus?.unknownCarrier ?? 0} unknown)
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Carrier</th>
                                    <th>Audits</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(corpus?.carriers || []).length === 0 && (
                                    <tr><td colSpan={2} style={{ color: 'var(--text-muted)' }}>No carriers detected yet.</td></tr>
                                )}
                                {(corpus?.carriers || []).map(c => (
                                    <tr key={c.name}>
                                        <td>{c.name}</td>
                                        <td style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{c.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard;
